import { useQuery } from '@tanstack/react-query';
import { formatHours, formatMonthYear, type CustomerMonthlyHoursResponse } from '@codepass/shared';
import { api } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle, Skeleton } from '@/components/ui';

/**
 * Kortet 'Timer pr. måned' på /kunder/:id: timerne registreret i OnePro på kundens arbejde de
 * seneste 12 måneder, nyeste først, og hvor mange af dem der ligger på en faktureret opgave.
 * Tallene er serverens - se customer-hours.ts.
 */
export function MonthlyHoursCard({ customerKey }: { customerKey: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['customer', customerKey, 'monthly-hours'],
    queryFn: () =>
      api.get<CustomerMonthlyHoursResponse>(
        `/customers/${encodeURIComponent(customerKey)}/monthly-hours`,
      ),
    enabled: Boolean(customerKey),
  });

  const invoicedTotal = data?.months.reduce((total, month) => total + month.invoiced_hours, 0) ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Timer pr. måned</CardTitle>
      </CardHeader>
      <CardBody>
        {isLoading || !data ? (
          <Skeleton className="h-32" />
        ) : !data.linked ? (
          <p className="text-xs text-ink-muted">
            Kunden er ikke koblet til en tavle eller et firma i OnePro, så timerne kan ikke findes.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 gap-y-1 text-xs">
              <span className="text-ink-muted">Måned</span>
              <span className="text-right text-ink-muted">Timer</span>
              <span className="text-right text-ink-muted">Faktureret</span>
              {[...data.months].reverse().map((month) => (
                <MonthRow key={month.month} month={month} />
              ))}
              <span className="border-t border-line pt-1 font-medium">I alt</span>
              <span className="border-t border-line pt-1 text-right font-medium tabular">
                {formatHours(data.total_hours)}
              </span>
              <span className="border-t border-line pt-1 text-right tabular">
                {formatHours(invoicedTotal)}
              </span>
            </div>
            <p className="mt-2 text-xs text-ink-muted">
              Registreret tid i OnePro i den måned, arbejdet blev lavet. Faktureret er timerne på
              opgaver, der står som faktureret.
            </p>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function MonthRow({ month }: { month: CustomerMonthlyHoursResponse['months'][number] }) {
  return (
    <>
      <span>{formatMonthYear(`${month.month}-15`)}</span>
      <span className="text-right tabular">{formatHours(month.hours)}</span>
      <span className="text-right tabular text-ink-muted">{formatHours(month.invoiced_hours)}</span>
    </>
  );
}
